/**
 * capture.ts — the `CaptureManifest` (manifest.json): the RAW, on-disk stitch input.
 *
 * ⚠️ THIS IS NOT THE CANONICAL TOUR CONTRACT. `Tour` (tour.ts) is the published,
 * immutable viewer artifact; `CaptureManifest` is the *input* the on-device stitch (and
 * the Node CLI) CONSUME. It is the serialized form of the device's `[CapturedShot]` —
 * today only passed in-memory to `reprojectToEquirect` — written to disk so payload B
 * (raw stills + poses) is cloud-stitchable and so the same bytes can be reproduced
 * cross-language. It is **not** `tour.json`, and adding fields here never changes the
 * `Tour` schema. See docs/CAPTURE_PIPELINE.md and docs/CONTRACT.md.
 *
 * ── WHO WRITES / READS IT ─────────────────────────────────────────────────────────────
 *   WRITTEN BY: the device, one `manifest.json` per node, next to that node's JPEGs in a
 *               per-node temp/Documents dir
 *               (`modules/pisorama-capture/.../<roomId>/manifest.json`). It mirrors the
 *               Swift `CapturedShot` array (GuidedPanoController) field-for-field.
 *   READ BY:    (a) the on-device pose-aided reprojection (Swift
 *               `GuidedPanoController.reprojectToEquirect`), and (b) the Node CLI
 *               (`tools/pano/reproject.mjs`, whose `PoseTaggedFrame` is the same shape),
 *               which reads `manifest.json` + the sibling JPEGs and emits the SAME
 *               equirect Swift produces — the cross-language fixture for constraint #4.
 *
 * ── INVARIANTS (load-bearing, IDENTICAL Node ↔ Swift) ─────────────────────────────────
 *   • `orientation` is the CAMERA→WORLD unit quaternion `[x,y,z,w]`
 *     (= `simd_quatf(frame.camera.transform)`). Reprojection rotates world→camera with
 *     its CONJUGATE.
 *   • Camera looks −Z, +Y up: a point in front of the camera has `Zc < 0`.
 *   • `intrinsics` are ALWAYS expressed in THIS saved JPEG's pixel space. The rescale
 *     `K *= savedSize / camera.imageResolution` happens at WRITE time on device (Swift),
 *     or at READ time in the Node CLI — never both. So `fx,fy,cx,cy` already match
 *     `width`/`height` below.
 *   • Angles are RADIANS, positions are METRES — never degrees, never normalized 0..1.
 *   • `worldPos` / `baseYaw` are in the ARKit world frame (+Y up). `baseYaw` is NOT baked
 *     into the equirect bytes; the viewer folds it in via `TourNode.defaultYaw`.
 *
 * ── WHY ZOD HERE (unlike firestore.ts, which is plain interfaces) ─────────────────────
 *   `manifest.json` crosses a process / language / disk boundary (Swift writes it, Node
 *   reads it). It is untrusted JSON at read time, so it gets a runtime-validating schema
 *   (a malformed manifest must fail loudly at the CLI, not miscompute a pano). The
 *   Firestore docs are typed at the call site and stay plain TS.
 */
import { z } from 'zod';
/** Schema version of `manifest.json`. Bump on a breaking change to the on-disk shape. */
export const CAPTURE_MANIFEST_VERSION = 1;
/**
 * A 3D position in METRES, ARKit world space (right-handed, +Y up).
 * Mirrors the on-device `WorldPos` and `RawNodeDoc.worldPos`. NOT the floor-plan plane:
 * the floor-plan coord is later derived as `{ x: worldPos.x, y: worldPos.z }`.
 */
export const Vec3 = z.object({ x: z.number(), y: z.number(), z: z.number() });
/**
 * Pinhole camera intrinsics, in the SAVED JPEG's pixel space (already rescaled from
 * `frame.camera.intrinsics` by `savedSize / frame.camera.imageResolution`).
 *
 * From ARKit's 3×3 `K`: `fx = K[0][0]`, `fy = K[1][1]`, `cx = K[0][2]`, `cy = K[1][2]`.
 * Used by the reprojection to project a world ray to a pixel:
 *   `zc = −dirCam.z;  u = cx + fx·dirCam.x/zc;  v = cy − fy·dirCam.y/zc`  (image-y down).
 */
export const Intrinsics = z.object({
    /** Focal length in pixels, x. */
    fx: z.number(),
    /** Focal length in pixels, y. */
    fy: z.number(),
    /** Principal point x, in pixels. */
    cx: z.number(),
    /** Principal point y, in pixels. */
    cy: z.number(),
});
/**
 * A camera orientation as a UNIT quaternion `[x, y, z, w]`, CAMERA→WORLD
 * (`v_world = q · v_cam`), exactly `simd_quatf(frame.camera.transform)`. Stored as a
 * 4-tuple (not an object) to match the Swift/Node array form and the viewer's three.js
 * convention. Reprojection uses its CONJUGATE to rotate world→camera.
 */
export const Pose = z.tuple([z.number(), z.number(), z.number(), z.number()]);
/**
 * One accepted guided shot: a JPEG + the pose ARKit reported when it was taken. The
 * canonical, persisted form of one Swift `CapturedShot` / one Node `PoseTaggedFrame`.
 * ≈62 of these per node (12 yaw × 5 pitch bands + 2 pole caps).
 */
export const CaptureFrame = z.object({
    /** Filename of the JPEG (of `frame.capturedImage`), RELATIVE to the manifest dir. */
    file: z.string().min(1),
    /** Pixel width of THIS saved JPEG (intrinsics are already in this pixel space). */
    width: z.number().int().positive(),
    /** Pixel height of THIS saved JPEG. */
    height: z.number().int().positive(),
    /** CAMERA→WORLD unit quaternion `[x,y,z,w]` = `simd_quatf(frame.camera.transform)`. */
    orientation: Pose,
    /** Pinhole intrinsics in THIS JPEG's pixels (rescaled from `frame.camera.intrinsics`). */
    intrinsics: Intrinsics,
    /**
     * Capture time in SECONDS (`frame.timestamp`) — brief constraint #2. Used only to
     * order frames / drop stale ones; NOT used by the reprojection geometry.
     */
    timestamp: z.number(),
    /**
     * Derived camera yaw (RADIANS, gravity-aligned, three.js: 0 = +X, CCW). Convenience for
     * coverage/debug + the reticle; NOT used by reprojection (which uses `orientation`).
     */
    yaw: z.number(),
    /** Derived camera pitch (RADIANS, + is up). Convenience/debug only, like `yaw`. */
    pitch: z.number(),
    /**
     * ARKit camera centre for this shot (METRES, world, = `frame.camera.transform` column 3).
     * OPTIONAL (absent on manifests written before 2026-10-03). The seam-aware stitch
     * (`tools/pano/stitch.mjs`, Swift `EquirectReprojector.reprojectSeamAware`) uses it,
     * relative to the centroid of all shots, to remove the parallax of a body-pivot sweep.
     */
    position: Vec3.optional(),
    /**
     * Which camera path produced the JPEG. OPTIONAL (absent = 'video', every manifest
     * before 2026-10-03): 'video' = the ARKit video frame (1920×1440); 'still' = an
     * ARKit high-resolution capture (`captureHighResolutionFrame`, iOS 16+, 12 MP on an
     * iPhone 14) downscaled at capture time. Pose and intrinsics are the still's own.
     */
    source: z.enum(['video', 'still']).optional(),
    /**
     * ARKit `camera.trackingState` of the frame the pose came from. OPTIONAL (absent on
     * manifests written before 2026-10-03). The guided sweep only fires on 'normal'; a
     * high-res still's own frame can still report 'limited'. Diagnostics for stitch
     * debugging (a drifted top ring), not used by the geometry.
     */
    trackingState: z.enum(['normal', 'limited', 'notAvailable']).optional(),
    /**
     * LiDAR phones (2026-10-28): this shot's ARKit `sceneDepth` beside the JPEG — raw
     * little-endian UInt16 millimetres of z-depth, 0 = no data. Taken from the trigger video
     * frame, so it carries that frame's own orientation, position and intrinsics (in DEPTH
     * pixels): a high-res still's field of view can differ. OPTIONAL (no LiDAR, older apps).
     * Read by tools/pano `readDepthU16` / EquirectReprojector `readDepthU16`.
     */
    depth: z
        .object({
        file: z.string().regex(/^[\w.-]+\.u16$/),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
        intrinsics: Intrinsics.optional(),
        orientation: Pose.optional(),
        position: Vec3.optional(),
    })
        .optional(),
});
/**
 * Per-node header: the standpoint + heading + coverage for one room/standpoint.
 * Mirrors the device `RawCaptureNode` (id/roomId/name/order/worldPos/baseYaw/coverage),
 * plus capture provenance (`capturedAt`). `worldPos` is OPTIONAL: present on device
 * (ARKit knows the standpoint), omitted when reprojecting frames whose standpoint is
 * unknown (e.g. the Node fixture). Translation is otherwise ignored by the stitch.
 */
export const CaptureNode = z.object({
    /** Node id == `RawCaptureNode.id`, e.g. "node_<roomId>". */
    id: z.string().min(1),
    /** User label (Spanish), e.g. "Salón". */
    name: z.string().default(''),
    /** RoomPlan room id this node was captured in. */
    roomId: z.string().min(1),
    /** Visiting order during capture (seeds the node graph). */
    order: z.number().int().nonnegative().default(0),
    /**
     * OPTIONAL standpoint, METRES, ARKit world (+Y up). Present on device; omit if unknown.
     * The pano placement on the floor plan derives from this; the stitch ignores it.
     */
    worldPos: Vec3.optional(),
    /**
     * Heading at frame 0, RADIANS (three.js: 0 = +X, CCW) — the pano's "north". NOT baked
     * into the equirect bytes; folded in at the viewer via `TourNode.defaultYaw`.
     */
    baseYaw: z.number().default(0),
    /** Guided-sphere coverage, 0..1 (1 = full sphere; <1 = gaps the UI warns about). */
    coverage: z.number().min(0).max(1).default(0),
    /** ISO-8601 capture timestamp for this node. */
    capturedAt: z.string(),
});
/**
 * The floor plane ARKit detected during the sweep (plane detection runs on every device,
 * no LiDAR needed). OPTIONAL top-level manifest field (absent before 2026-10-17, or when
 * no floor was seen). Chosen on device (`PlaneLayout.swift`): the largest plane classified
 * `.floor` 0.5–2.5 m below the median camera height, else the lowest unclassified
 * horizontal plane ≥ 0.5 m² in that band.
 */
export const CaptureFloor = z.object({
    /** Floor height, METRES, same ARKit world frame as `CaptureFrame.position` (+Y up). */
    y: z.number(),
    /** Area of the detected floor polygon, m² (how much of the floor ARKit saw). */
    extentArea: z.number().nonnegative(),
    /** true when ARKit classified the plane `.floor` (false = unclassified fallback). */
    classified: z.boolean(),
    /** Where the floor came from. Only ARKit plane anchors today. */
    source: z.literal('planeAnchor'),
});
/**
 * One ARKit vertical plane seen during the sweep — a cross-check for a room-layout model
 * that reads the walls from the equirect. Same world frame as `CaptureFrame.position`.
 */
export const CaptureWall = z.object({
    /** Centre of the wall patch, METRES (horizontal midpoint, vertical midpoint). */
    center: Vec3,
    /** Unit normal in the XZ plane, pointing toward the sweep (the room interior). */
    normal: z.object({ x: z.number(), z: z.number() }),
    /** Horizontal extent along the wall, metres. */
    width: z.number().nonnegative(),
    /** Vertical extent, metres. */
    height: z.number().nonnegative(),
    /** true when ARKit classified the plane `.wall`. */
    classified: z.boolean(),
});
/**
 * `CaptureManifest` (v1) — the whole `manifest.json`: one node header + its accepted
 * frames. The canonical on-disk artifact the device WRITES and the stitch CONSUMES; the
 * cross-language fixture between Swift `reprojectToEquirect` and the Node CLI.
 */
export const CaptureManifest = z.object({
    /** Schema version (`CAPTURE_MANIFEST_VERSION`). */
    version: z.literal(CAPTURE_MANIFEST_VERSION),
    /** Per-node header (standpoint, heading, coverage, provenance). */
    node: CaptureNode,
    /** One entry per accepted guided shot, in capture order (≈62). */
    frames: z.array(CaptureFrame).min(1),
    /** OPTIONAL detected floor plane (see `CaptureFloor`). */
    floor: CaptureFloor.optional(),
    /**
     * OPTIONAL camera height above `floor`, METRES: median of `frame.position.y − floor.y`
     * over the frames. Present only with `floor`. The metric scale for a layout model
     * that estimates the room from the pano up to scale.
     */
    cameraHeight: z.number().positive().optional(),
    /** OPTIONAL ARKit vertical planes, largest first, at most 24 (see `CaptureWall`). */
    walls: z.array(CaptureWall).max(24).optional(),
});
/** Parse + validate an unknown payload into a `CaptureManifest`, throwing on invalid input. */
export function parseCaptureManifest(input) {
    return CaptureManifest.parse(input);
}
/** Non-throwing variant (returns a typed result) for the CLI's friendly error path. */
export function safeParseCaptureManifest(input) {
    return CaptureManifest.safeParse(input);
}
//# sourceMappingURL=capture.js.map